import {
  Stack,
  Box,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Typography,
  Button,
  IconButton,
  Menu,
  ListItemText,
  MenuItem,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
  handleMedicationDelete,
  QuestionSlice,
} from "../../../Redux/reducers/responseSlice";
import { useParams } from "react-router-dom";
import { NoDataContainer, StyledTableCell } from "../../Common/styles/table";
import { useState } from "react";
import { MoreVert } from "@mui/icons-material";
import MedicationModal from "./MedicationModal";
import { triggerSave } from "../../../Redux/actions/responseEventAction";
import { useTranslation } from "react-i18next";

type Props = {
  currentQuestion: QuestionSlice;
};

const MedicationField = ({ currentQuestion }: Props) => {
  const { t } = useTranslation("responses");
  const { id: studyId, surveySlug } = useParams();
  const dispatch = useAppDispatch();
  const editable = useAppSelector((state) => state.response.formEditable);

  const { responses } = currentQuestion;
  const data = responses?.[0]?.medications || [];

  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [showModal, setShowModal] = useState(false);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    row: any,
    index: number
  ) => {
    setSelectedRow({
      ...row,
      index,
    });
    setAnchorEl(event.currentTarget);
  };

  const handleEdit = () => {
    setAnchorEl(null);
    setShowModal(true);
  };

  const handleDelete = () => {
    if (selectedRow) {
      dispatch(
        handleMedicationDelete({
          id: currentQuestion.id,
          index: selectedRow.index,
        })
      );
      setAnchorEl(null);
      setSelectedRow(null);
      dispatch(
        triggerSave({
          studyId,
          id: currentQuestion.id,
          surveySlug,
        })
      );
    }
  };

  const closeModal = () => {
    setSelectedRow(null);
    setShowModal(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const addMedication = () => {
    setSelectedRow(null);
    setAnchorEl(null);
    setShowModal(true);
  };

  return (
    <Stack
      sx={{
        width: {
          xs: "100%",
          md: "100%",
        },
        pl: "60px",
      }}
      gap={1}
    >
      {editable && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button variant="contained" onClick={addMedication}>
            {t("TYPE_BASED.ADD_MEDICATION")}
          </Button>
        </Box>
      )}
      <Box sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("TYPE_BASED.MEDICATION_NAME")}</StyledTableCell>
              <StyledTableCell>{t("TYPE_BASED.MEDICATION_ATC_CODE")}</StyledTableCell>
              <StyledTableCell>{t("TYPE_BASED.MEDICATION_ROUTE_OF_ADMINISTRATION")}</StyledTableCell>
              <StyledTableCell>{t("TYPE_BASED.MEDICATION_FORM")}</StyledTableCell>
              <StyledTableCell>{t("TYPE_BASED.MEDICATION_DOSE")}</StyledTableCell>
              {<StyledTableCell></StyledTableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((row: any, index: number) => (
              <TableRow key={row?.name + index}>
                <StyledTableCell>{row?.name || "-"}</StyledTableCell>
                <StyledTableCell>{row?.code || "-"}</StyledTableCell>
                <StyledTableCell>{row?.route || "-"}</StyledTableCell>
                <StyledTableCell>{row?.form || "-"}</StyledTableCell>
                <StyledTableCell>{row?.dose || "-"}</StyledTableCell>
                {editable && (
                  <StyledTableCell>
                    <IconButton onClick={(e) => handleMenuClick(e, row, index)}>
                      <MoreVert />
                    </IconButton>
                  </StyledTableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.length === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              {t("SURVEY.NO_DATA_AVAILABLE")}
            </Typography>
          </NoDataContainer>
        )}
      </Box>
      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleEdit();
            setAnchorEl(null);
          }}
        >
          <ListItemText>{t("SURVEY.MENU_EDIT")}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDelete();
          }}
        >
          <ListItemText>{t("DELETE", { ns: "common" })}</ListItemText>
        </MenuItem>
      </Menu>
      {showModal && (
        <MedicationModal
          showModal={showModal}
          closeModal={closeModal}
          data={selectedRow}
          questionId={currentQuestion.id}
        />
      )}
    </Stack>
  );
};
export default MedicationField;
